import { publicBotsFilters } from "@shared/slices/filters.slice";
import {
  Direction,
  FilterType,
  LongShort,
  GetPublicBotsRequest,
} from "@shared/types";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { BotsTab } from "@shared/features/bots/types";
import { useFilterBotsBySearch } from "@shared/features/bots/hooks";
import { sharedLib } from "@shared/services/shared.service";

const usePublicBots = (
  searchValue?: string,
  onLoadingChange?: (isLoading: boolean) => void
) => {
  const [showLoading, setShowLoading] = useState(false);
  const filtersData = useSelector(
    publicBotsFilters.selectors.selectFiltersData
  );

  const consecutiveActiveDaysFilter =
    filtersData[FilterType.CONSECUTIVE_ACTIVE_DAYS];
  const symbolsFilter = filtersData[FilterType.SYMBOLS];
  const exchangesFilter = filtersData[FilterType.EXCHANGES];
  const longShortFilter = filtersData[FilterType.LONG_SHORT];
  const validBotAmountFilter = filtersData[FilterType.VALID_BOT_AMOUNT];

  let longShortArg: LongShort | undefined = undefined;
  if (
    longShortFilter?.value.includes(Direction.LONG) &&
    !longShortFilter?.value.includes(Direction.SHORT)
  ) {
    longShortArg = LongShort.LONG;
  } else if (
    longShortFilter?.value.includes(Direction.SHORT) &&
    !longShortFilter?.value.includes(Direction.LONG)
  ) {
    longShortArg = LongShort.SHORT;
  }

  const emptyFilters =
    !exchangesFilter?.value.length ||
    !longShortFilter?.value.length ||
    !symbolsFilter?.value.length ||
    !consecutiveActiveDaysFilter?.value.length;

  const getFilters = () => {
    if (emptyFilters) return {};
    const filters: GetPublicBotsRequest = {};
    if (exchangesFilter?.value.length !== exchangesFilter?.options.length) {
      filters.exchanges = exchangesFilter.value;
    }
    if (longShortArg) {
      filters.long_short = longShortArg;
    }
    if (
      consecutiveActiveDaysFilter?.value.length &&
      consecutiveActiveDaysFilter?.options.length &&
      (consecutiveActiveDaysFilter?.value[0] !==
        consecutiveActiveDaysFilter?.options[0] ||
        consecutiveActiveDaysFilter?.value[1] !==
          consecutiveActiveDaysFilter?.options[1])
    ) {
      filters.days_running = {
        min:
          consecutiveActiveDaysFilter.value[0] ||
          consecutiveActiveDaysFilter?.options[0],
        max:
          consecutiveActiveDaysFilter.value[1] ||
          consecutiveActiveDaysFilter?.options[1],
      };
    }
    if (symbolsFilter?.value.length !== symbolsFilter?.options.length) {
      filters.symbols = symbolsFilter.value;
    }
    if (!validBotAmountFilter?.value) {
      filters.valid_bot_amount_only = false;
    }
    return filters;
  };

  const args = useMemo(() => {
    if (emptyFilters && (exchangesFilter?.options.length || 0) > 0)
      return skipToken;
    return getFilters();
  }, [
    emptyFilters,
    longShortArg,
    consecutiveActiveDaysFilter,
    symbolsFilter,
    validBotAmountFilter,
  ]);

  const getPublicBotsQuery =
    sharedLib.sharedApi.bots.useGetPublicBotsQuery(args);
  const { data, ...status } = getPublicBotsQuery;

  useEffect(() => {
    setShowLoading(true);
    setTimeout(() => setShowLoading(false), 500);
  }, [status.startedTimeStamp]);

  const bots = data?.data;
  const filteredBots = useFilterBotsBySearch(
    bots,
    searchValue,
    BotsTab.PUBLIC_BOTS
  );

  const emptyData =
    emptyFilters ||
    (!getPublicBotsQuery.isUninitialized &&
      !getPublicBotsQuery.isLoading &&
      !filteredBots?.length);

  const isFetching = showLoading || getPublicBotsQuery.isFetching;

  useEffect(() => {
    onLoadingChange?.(isFetching);
  }, [isFetching]);

  const refetch = () => {
    try {
      getPublicBotsQuery.refetch();
    } catch {}
  };

  return {
    isFetching,
    isLoading: getPublicBotsQuery.isLoading,
    isUninitialized: getPublicBotsQuery.isUninitialized,
    filteredBots: filteredBots || [],
    emptyData,
    emptyFilters,
    refetch,
  };
};

export default usePublicBots;
